import type { TokenPair } from '@aawas/contracts';
import { Building2, KeyRound, Phone } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useEffect, useState, type FormEvent } from 'react';
import { api, ApiError, setSession } from '../lib/api';
import { Button, Field, Input } from '../ui';

const digits = (s: string) => s.replace(/\D/g, '').replace(/^(91|0)(?=\d{10}$)/, '');

export function Login() {
  const [step, setStep] = useState<'phone' | 'code'>('phone');
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cooldown, setCooldown] = useState(0);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const phoneOk = /^[6-9]\d{9}$/.test(digits(phone));

  async function requestCode(e?: FormEvent) {
    e?.preventDefault();
    if (!phoneOk) return setError('Enter your 10-digit mobile number');
    setBusy(true);
    setError(null);
    try {
      const r = await api<{ retryAfterSec: number }>('/auth/otp/request', { body: { phone: digits(phone) }, anon: true });
      setCooldown(r.retryAfterSec);
      setStep('code');
    } catch (err) {
      // Rate-limited resend still lets them enter a code they already have.
      if (err instanceof ApiError && err.code === 'RATE_LIMITED') setStep('code');
      else setError(err instanceof Error ? err.message : 'Could not send the code');
    } finally {
      setBusy(false);
    }
  }

  async function verify(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const s = await api<TokenPair>('/auth/otp/verify', {
        body: { phone: digits(phone), code, deviceLabel: navigator.userAgent.slice(0, 60) },
        anon: true,
      });
      setSession(s);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'That code did not work');
      setCode('');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex min-h-dvh flex-col items-center justify-center bg-gradient-to-b from-brand-700 to-violet-700 px-5" style={{ paddingTop: 'var(--safe-top)', paddingBottom: 'var(--safe-bottom)' }}>
      <div className="w-full max-w-sm">
        <div className="mb-6 text-center text-white">
          <div className="mx-auto mb-3 flex h-16 w-16 items-center justify-center rounded-2xl bg-white/15">
            <Building2 className="h-9 w-9" aria-hidden />
          </div>
          <h1 className="text-3xl font-bold tracking-tight">Expansio Aawas</h1>
          <p className="mt-1 text-white/80">Your society, in your pocket</p>
        </div>

        <div className="rounded-3xl bg-white p-6 shadow-xl">
          {step === 'phone' ? (
            <form onSubmit={requestCode} className="space-y-4" noValidate>
              <Field label="Mobile number" hint="Use the number registered with your society" error={error ?? undefined}>
                <div className="relative">
                  <Phone className="pointer-events-none absolute left-3.5 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" aria-hidden />
                  <Input inputMode="numeric" autoComplete="tel-national" autoFocus placeholder="98765 43210" className="pl-11" value={phone} onChange={(e) => setPhone(e.target.value)} maxLength={16} />
                </div>
              </Field>
              <Button type="submit" size="lg" className="w-full" loading={busy} disabled={!phoneOk}>
                Continue
              </Button>
            </form>
          ) : (
            <form onSubmit={verify} className="space-y-4" noValidate>
              <p className="text-sm text-slate-700">
                Enter the 6-digit code for <b>+91 {digits(phone)}</b>. If your society gave you a one-time code, use that.
              </p>
              <Field label="6-digit code" error={error ?? undefined}>
                <div className="relative">
                  <KeyRound className="pointer-events-none absolute left-3.5 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" aria-hidden />
                  <Input inputMode="numeric" autoComplete="one-time-code" autoFocus placeholder="••••••" className="pl-11 text-center text-2xl tracking-[0.4em]" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))} maxLength={6} />
                </div>
              </Field>
              <Button type="submit" size="lg" className="w-full" loading={busy} disabled={code.length !== 6}>
                Sign in
              </Button>
              <div className="flex items-center justify-between text-sm">
                <button type="button" className="font-medium text-slate-600 underline" onClick={() => { setStep('phone'); setError(null); setCode(''); }}>
                  Change number
                </button>
                <button type="button" disabled={cooldown > 0 || busy} className="font-medium text-brand-700 underline disabled:text-slate-400 disabled:no-underline" onClick={() => requestCode()}>
                  {cooldown > 0 ? `Resend in ${cooldown}s` : 'Resend code'}
                </button>
              </div>
            </form>
          )}
        </div>
        <p className="mt-5 text-center text-white">
          New society? <Link to="/signup" className="font-semibold underline">Register your society</Link>
        </p>
        <p className="mt-4 text-center text-xs text-white/70">
          Only residents and staff registered by their society can sign in. Your phone number and visitor photos are used only to run your society's gate and helpdesk.
        </p>
      </div>
    </div>
  );
}
